import React from 'react';
import { createPortal } from 'react-dom';
import themes from '../themes';

/** Viewport layer: above in-app overlays; portal avoids parent transform/backdrop-filter offsetting fixed modals */
const MODAL_Z_INDEX = 100000;

/**
 * Modal for errors, warnings, info, and success (e.g. confirmed trade / claim).
 * File name kept as ErrorModal for imports; used for all result types.
 */
function ErrorModal({
  isOpen,
  onClose,
  title,
  message,
  type = 'error',
  onCloseCallback,
  linkUrl,
  linkLabel,
  detail,
  onConfirm,
  confirmLabel = 'Continue',
  cancelLabel = 'Cancel'
}) {
  const theme = themes.darkLuxury.colors;

  if (!isOpen) return null;
  
  // Handle close with optional callback (X, backdrop, Cancel, OK)
  const handleClose = () => {
    onClose();
    if (onCloseCallback) {
      onCloseCallback();
    }
  };

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
    onClose();
  };

  const isConfirmFlow = typeof onConfirm === 'function';

  // Color scheme based on type
  const typeStyles = {
    error: {
      icon: '❌',
      color: theme.danger,
      gradient: 'linear-gradient(135deg, rgba(244, 67, 54, 0.1) 0%, rgba(211, 47, 47, 0.1) 100%)',
      border: '2px solid rgba(244, 67, 54, 0.3)'
    },
    warning: {
      icon: '⚠️',
      color: theme.warning,
      gradient: 'linear-gradient(135deg, rgba(255, 193, 7, 0.1) 0%, rgba(255, 152, 0, 0.1) 100%)',
      border: '2px solid rgba(255, 193, 7, 0.3)'
    },
    info: {
      icon: 'ℹ️',
      color: theme.primary,
      gradient: 'linear-gradient(135deg, rgba(100, 255, 218, 0.1) 0%, rgba(79, 214, 184, 0.1) 100%)',
      border: '2px solid rgba(100, 255, 218, 0.3)'
    },
    success: {
      icon: '✅',
      color: theme.success,
      gradient: 'linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(56, 142, 60, 0.1) 100%)',
      border: '2px solid rgba(76, 175, 80, 0.3)'
    }
  };

  const currentStyle = typeStyles[type] || typeStyles.error;

  // Clean up error messages (remove technical details for user-rejected transactions)
  const cleanMessage = (msg) => {
    if (!msg) return 'An error occurred';
    
    // User rejected transaction
    if (msg.includes('User rejected') || msg.includes('User denied') || msg.includes('rejected the request')) {
      return 'Transaction cancelled';
    }
    
    // Remove technical stack traces and unhelpful details
    const cleaned = msg
      .replace(/Request Arguments:[\s\S]*$/g, '') // Remove request arguments
      .replace(/Error: /g, '')
      .replace(/MetaMask Tx Signature: /g, '')
      .trim();
    
    return cleaned || 'An error occurred';
  };

  // Get user-friendly title
  const getTitle = () => {
    if (title) return title;
    
    if (type === 'error') return 'Transaction Failed';
    if (type === 'warning') return 'Warning';
    if (type === 'info') return 'Information';
    if (type === 'success') return 'Success';
    return 'Notice';
  };

  // Get user-friendly description for common errors
  const getUserFriendlyMessage = (msg) => {
    const cleaned = cleanMessage(msg);
    
    // Don't parse success / info / confirm copy as errors (and don't truncate it)
    if (type === 'success' || type === 'info' || detail || isConfirmFlow) {
      return {
        main: cleaned,
        sub: null
      };
    }
    
    // User cancelled
    if (cleaned === 'Transaction cancelled') {
      return {
        main: 'Transaction Cancelled',
        sub: 'You cancelled the transaction in your wallet. No funds were transferred.'
      };
    }
    
    // Insufficient balance
    if (cleaned.includes('insufficient') || cleaned.includes('balance')) {
      return {
        main: 'Insufficient Balance',
        sub: 'You don\'t have enough USDC or MATIC to complete this transaction.'
      };
    }
    
    // Gas issues
    if (cleaned.includes('gas') || cleaned.includes('Gas')) {
      return {
        main: 'Transaction Failed',
        sub: 'The transaction ran out of gas or gas price was too low. Please try again.'
      };
    }
    
    // Network issues
    if (cleaned.includes('network') || cleaned.includes('connection')) {
      return {
        main: 'Network Error',
        sub: 'Connection to the blockchain failed. Please check your internet and try again.'
      };
    }
    
    // Default
    return {
      main: cleaned.length > 100 ? cleaned.substring(0, 100) + '...' : cleaned,
      sub: cleaned.length > 100 ? 'Check console for details' : null
    };
  };

  const friendlyMsg = getUserFriendlyMessage(message);

  const modalTree = (
    <>
      {/* Backdrop — portaled to body so centering is viewport-based (parents with backdrop-filter/transform no longer shift it) */}
      <div
        onClick={handleClose}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          backdropFilter: 'blur(4px)',
          zIndex: MODAL_Z_INDEX,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          animation: 'fadeIn 0.2s ease-out'
        }}
      >
        {/* Modal */}
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            background: currentStyle.gradient,
            border: currentStyle.border,
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
            animation: 'slideIn 0.3s ease-out',
            position: 'relative'
          }}
        >
          {/* Close button */}
          <button
            onClick={handleClose}
            style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              background: 'none',
              border: 'none',
              color: theme.textSecondary,
              fontSize: '24px',
              cursor: 'pointer',
              padding: '4px',
              lineHeight: '1',
              transition: 'color 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.color = theme.textPrimary}
            onMouseLeave={(e) => e.target.style.color = theme.textSecondary}
          >
            ×
          </button>

          {/* Icon */}
          <div style={{ 
            fontSize: '48px', 
            textAlign: 'center', 
            marginBottom: '16px'
          }}>
            {currentStyle.icon}
          </div>

          {/* Title */}
          <h2 style={{
            color: currentStyle.color,
            fontSize: '24px',
            fontWeight: '700',
            textAlign: 'center',
            marginBottom: '12px',
            margin: 0
          }}>
            {getTitle()}
          </h2>

          {/* Message */}
          <p style={{
            color: theme.textPrimary,
            fontSize: '16px',
            textAlign: 'center',
            marginBottom: '8px',
            lineHeight: '1.5'
          }}>
            {friendlyMsg.main}
          </p>

          {/* Sub message */}
          {friendlyMsg.sub && (
            <p style={{
              color: theme.textSecondary,
              fontSize: '14px',
              textAlign: 'center',
              marginBottom: detail ? '12px' : '24px',
              lineHeight: '1.4'
            }}>
              {friendlyMsg.sub}
            </p>
          )}

          {detail && (
            <p style={{
              color: theme.textSecondary,
              fontSize: '14px',
              textAlign: 'center',
              marginTop: '4px',
              marginBottom: '24px',
              lineHeight: '1.5',
              whiteSpace: 'pre-line'
            }}>
              {detail}
            </p>
          )}

          {linkUrl && (
            <div style={{ textAlign: 'center', marginTop: '10px' }}>
              <a
                href={linkUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: theme.primary,
                  fontSize: '14px',
                  fontWeight: '600',
                  textDecoration: 'underline'
                }}
              >
                {linkLabel || 'View transaction on PolygonScan'}
              </a>
            </div>
          )}

          {isConfirmFlow ? (
            <>
              <button
                onClick={handleConfirm}
                style={{
                  width: '100%',
                  padding: '14px',
                  background: theme.buttonBg,
                  color: theme.buttonText,
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  boxShadow: '0 4px 12px rgba(100, 255, 218, 0.2)',
                  marginTop: '8px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = theme.buttonHover;
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 16px rgba(100, 255, 218, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = theme.buttonBg;
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 12px rgba(100, 255, 218, 0.2)';
                }}
              >
                {confirmLabel}
              </button>
              <button
                onClick={handleClose}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: 'transparent',
                  color: theme.textSecondary,
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  marginTop: '4px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.color = theme.textPrimary;
                }}
                onMouseLeave={(e) => {
                  e.target.style.color = theme.textSecondary;
                }}
              >
                {cancelLabel}
              </button>
            </>
          ) : (
            <button
              onClick={handleClose}
              style={{
                width: '100%',
                padding: '14px',
                background: theme.buttonBg,
                color: theme.buttonText,
                border: 'none',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s',
                boxShadow: '0 4px 12px rgba(100, 255, 218, 0.2)',
                marginTop: '8px'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = theme.buttonHover;
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 16px rgba(100, 255, 218, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = theme.buttonBg;
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 12px rgba(100, 255, 218, 0.2)';
              }}
            >
              OK
            </button>
          )}

          {type === 'success' && (
            <p
              style={{
                color: theme.textSecondary,
                fontSize: '11px',
                textAlign: 'center',
                lineHeight: '1.45',
                margin: '14px 0 0 0',
                opacity: 0.85
              }}
            >
              * On-chain data can take up to 30 seconds to reflect on the frontend web page.
            </p>
          )}
        </div>
      </div>

      {/* Animations */}
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(-20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
      `}</style>
    </>
  );

  if (typeof document === 'undefined' || !document.body) {
    return null;
  }

  return createPortal(modalTree, document.body);
}

export default ErrorModal;

